'use client';

/* ============================================================================
 * TemplatesV2 - the six Instant Site templates in depth.
 * ============================================================================
 * Section order:
 *   1  Hero     - what a template means in Instant Site
 *   2  Gallery  - six template cards with CSS previews, body copy, best-for
 *   3  Shared   - what every template includes
 *   4  Closing  - demo CTA
 * Copy lives in messages/<locale>.json under Pages.Templates.
 * ========================================================================= */

import React from 'react';
import {useTranslations} from 'next-intl';
import {Link} from '@/i18n/navigation';
import {Reveal, RevealStagger, RevealLine} from '@/components/v2/Reveal';
import TemplateShowcase from '@/components/v2/templateShowcase/TemplateShowcase';

const NS = 'Pages.Templates';
const TOTAL = 4;

const TEMPLATE_SLUGS = ['classic', 'modern', 'editorial', 'minimal', 'resort', 'futuristic'];

function Container({children, className = ''}) {
  return <div className={`container-page ${className}`}>{children}</div>;
}

function SectionAnchor({id, label}) {
  return (
    <Reveal className="mb-10 flex items-center gap-3 text-foreground-subtle">
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">
        {String(id).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
      </span>
      <RevealLine className="block h-px flex-1 bg-border" delay={0.1} />
      <span className="font-mono text-[11px] tracking-[0.16em] uppercase">{label}</span>
    </Reveal>
  );
}

function CheckIcon({className = ''}) {
  return (
    <svg className={className} viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M11.5 4L5.5 10L2.5 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SectionHero() {
  const t = useTranslations(NS);
  return (
    <section className="bg-hero-mesh relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
      <Container>
        <SectionAnchor id={1} label={t('anchors.hero')} />
        <Reveal eager>
          <p className="fig-label">{t('hero.eyebrow')}</p>
        </Reveal>
        <Reveal delay={0.06} eager>
          <h1 className="headline-hero mt-5 max-w-[18ch] text-foreground">
            {t('hero.headline')}
          </h1>
        </Reveal>
        <Reveal delay={0.12} eager>
          <p className="lead mt-6 max-w-[62ch]">{t('hero.lead')}</p>
        </Reveal>
      </Container>
    </section>
  );
}

function SectionGallery() {
  const t = useTranslations(NS);
  const items = t.raw('items');
  return (
    <section className="section-rhythm-sm pb-24">
      <Container>
        <SectionAnchor id={2} label={t('anchors.gallery')} />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {items.map((item, i) => (
            <Reveal key={item.name} delay={(i % 2) * 0.06}>
              <article className="surface-elevated h-full overflow-hidden p-5 md:p-6">
                <Link href={`/templates/${TEMPLATE_SLUGS[i]}/`} aria-label={item.name}>
                  <TemplateShowcase slug={TEMPLATE_SLUGS[i]} />
                </Link>
                <div className="mt-6 flex items-baseline justify-between gap-4 px-1">
                  <h2 className="text-xl font-medium tracking-tight text-foreground md:text-2xl">
                    {item.name}
                  </h2>
                  <p className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-foreground-subtle">
                    {t('previewLabel')} {String(i + 1).padStart(2, '0')}
                  </p>
                </div>
                <p className="mt-1 px-1 text-[13px] font-medium tracking-tight text-primary">
                  {item.tagline}
                </p>
                <p className="mt-3 px-1 text-[14.5px] leading-relaxed text-foreground-muted">
                  {item.body}
                </p>
                <p className="mt-5 border-t border-border-subtle px-1 pt-4 text-[13px] leading-relaxed text-foreground-subtle">
                  <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase">
                    {t('bestForLabel')}:
                  </span>{' '}
                  {item.bestFor}
                </p>
                <Link
                  href={`/templates/${TEMPLATE_SLUGS[i]}/`}
                  className="cta-text-brand mt-5 px-1 text-[13px]"
                >
                  {t('detailCtaLabel')} <span aria-hidden>&rarr;</span>
                </Link>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}

function SectionShared() {
  const t = useTranslations(NS);
  const bullets = t.raw('shared.bullets');
  return (
    <section className="section-rhythm bg-background">
      <Container>
        <SectionAnchor id={3} label={t('anchors.shared')} />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-5">
            <Reveal>
              <h2 className="headline-section max-w-[18ch] text-foreground">
                {t('shared.headline')}
              </h2>
            </Reveal>
            <Reveal delay={0.06}>
              <p className="lead mt-5">{t('shared.lead')}</p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <RevealStagger
              as="ul"
              mode="clone"
              className="grid grid-cols-1 gap-x-8 gap-y-4 border-t border-border pt-6 sm:grid-cols-2"
              gap={0.05}
            >
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[14.5px] leading-relaxed text-foreground-muted">
                  <CheckIcon className="mt-1 size-3.5 flex-shrink-0 text-primary" />
                  <span>{b}</span>
                </li>
              ))}
            </RevealStagger>
          </div>
        </div>
      </Container>
    </section>
  );
}

function SectionClosing() {
  const t = useTranslations(NS);
  return (
    <section className="section-rhythm-lg">
      <Container>
        <SectionAnchor id={4} label={t('anchors.closing')} />
        <Reveal>
          <h2 className="headline-manifesto max-w-[20ch] text-foreground">
            {t('closing.headline')}
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="lead mt-6 max-w-[52ch]">{t('closing.body')}</p>
        </Reveal>
        <Reveal delay={0.14}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link href="/request-demo/" className="cta-pill-brand">
              {t('closing.primaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
            <Link href="/features/" className="cta-text">
              {t('closing.secondaryCta')} <span aria-hidden>&rarr;</span>
            </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}

export default function TemplatesV2() {
  return (
    <>
      <SectionHero />
      <SectionGallery />
      <SectionShared />
      <SectionClosing />
    </>
  );
}
